/**
 * Переводы интерфейса студии. Правило одно на весь портал:
 *
 * 1. Тексты раздела живут в `messages.ts` рядом с кодом:
 *      export const messages = defineMessages({
 *        ru: { title: 'Мои курсы', lessons: (n: number) => `${n} ${ruPlural(n, 'урок', 'урока', 'уроков')}` },
 *        en: { title: 'My courses', lessons: (n: number) => `${n} ${enPlural(n, 'lesson', 'lessons')}` },
 *      })
 *    Ключи и подписи функций у `en` обязаны совпадать с `ru` — пропущенный
 *    перевод ломает сборку, а не всплывает русским словом в английском UI.
 * 2. В компоненте — `const m = useMessages(messages)`, дальше `m.title`, `m.lessons(3)`.
 * 3. В обычной функции (не компонент: тексты ошибок, тостов) — `messagesNow(messages)`.
 *    Такая функция читает язык в момент вызова. Если её зовёт рендер, компонент
 *    обязан сам подписаться на язык (`useMessages` или `useUiLang`) — иначе после
 *    смены языка он не перерисуется и текст останется прежним.
 * 4. Даты и числа — `uiLocale()` (`ru-RU` / `en-US`) вместо зашитого `'ru-RU'`.
 *
 * Данные курса (названия, тексты уроков) — НЕ интерфейс: их язык выбирает автор,
 * переводить их здесь нельзя.
 */
import { useContext } from 'react'
import type { Lang } from '@/types/lang'
import { UiLangContext } from './uiLangContext'

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- подпись функции-сообщения произвольна
type MessageFn = (...args: any[]) => string
export type Message = string | MessageFn

/** Английская сторона повторяет русскую: тот же набор ключей и те же подписи функций. */
export type Mirror<T> = { [K in keyof T]: T[K] extends string ? string : T[K] }

export const defineMessages = <T extends Record<string, Message>>(m: {
  ru: T
  en: Mirror<T>
}): { ru: T; en: Mirror<T> } => m

let current: Lang = 'ru'

/** Текущий язык интерфейса (синхронизирует `LanguageProvider`). */
export const getUiLang = (): Lang => current

/** Только для `LanguageProvider` (и тестов). */
export const setUiLang = (lang: Lang): void => {
  current = lang
}

/** Тексты раздела на языке интерфейса — в компоненте. */
export const useMessages = <T extends Record<string, Message>>(m: { ru: T; en: Mirror<T> }): T => {
  const lang = useContext(UiLangContext) ?? current
  return (lang === 'en' ? m.en : m.ru) as T
}

/** Тексты раздела на языке интерфейса — вне компонента (ошибки, тосты, утилиты). */
export const messagesNow = <T extends Record<string, Message>>(m: { ru: T; en: Mirror<T> }): T =>
  (current === 'en' ? m.en : m.ru) as T

/** Локаль для `Intl` / `toLocaleString`. */
export const uiLocale = (): string => (current === 'en' ? 'en-US' : 'ru-RU')

/** Русское склонение по числу: 1 урок, 2 урока, 5 уроков. */
export const ruPlural = (n: number, one: string, few: string, many: string): string => {
  const abs = Math.abs(n) % 100
  const last = abs % 10
  if (abs > 10 && abs < 20) return many
  if (last > 1 && last < 5) return few
  if (last === 1) return one
  return many
}

/** Английское число: 1 lesson, 2 lessons. */
export const enPlural = (n: number, one: string, other: string): string =>
  Math.abs(n) === 1 ? one : other

/** Подписка компонента на язык интерфейса (когда своих текстов у него нет, но он
 *  рисует текст из `messagesNow`-функции). */
export const useUiLang = (): Lang => useContext(UiLangContext) ?? current

/** Тексты на ЗАДАННОМ языке — для экрана ученика в превью: плеер подписывает
 *  кнопки и заголовки на языке курса, а не на языке интерфейса автора. */
export const pickMessages = <T extends Record<string, Message>>(
  m: { ru: T; en: Mirror<T> },
  lang: Lang,
): T => (lang === 'en' ? m.en : m.ru) as T
